import React from 'react';

import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';

import { useTranslation } from 'next-i18next';

import Avatar from 'src/components/ui/Avatar';
import Button from 'src/components/ui/Button';
import Typography from 'src/components/ui/Typography';

import useAlerts from 'src/hooks/useAlerts';
import { useAddContactMutation } from 'src/hooks/__generated__/queries';
import type { Medium, UserProfileQuery } from 'src/types/__generated__/graphql';

interface Props {
    user: NonNullable<UserProfileQuery['userProfile']>;
    handleBack: () => void;
    handleNext: () => void;
}

const AddContactBlock: React.FC<Props> = ({ user, handleBack, handleNext }) => {
    const { t } = useTranslation(['profile', 'common']);
    const { addAlert } = useAlerts();

    const [addContactMutation] = useAddContactMutation();

    const handleRecommend = async () => {
        try {
            await addContactMutation({
                variables: {
                    userId: user.id,
                },
                refetchQueries: ['UserProfile'],
                awaitRefetchQueries: true,
            });
            addAlert({
                type: 'success',
                message: t('profile:addContactSuccess'),
            });
            handleNext();
        } catch (error: unknown) {
            addAlert({
                type: 'error',
                message: String((error as Error).message),
            });
        }
    };

    return (
        <>
            <Box display="flex" flexDirection="column" alignItems="center" p={3}>
                <Avatar
                    image={user.image as Medium}
                    alt={user.fullName}
                    fallbackText={user.initials}
                    size={64}
                    border
                />
                <Box mt={1}>
                    <Typography variant="h4" align="center">
                        {user.fullName}
                    </Typography>
                </Box>
            </Box>
            <Box mb={4} maxWidth="20rem" mx="auto">
                <Typography align="center">
                    <span
                        dangerouslySetInnerHTML={{
                            __html: t('profile:sendInviteConfirmation', {
                                fullName: user.fullName,
                            }),
                        }}
                    />
                </Typography>
            </Box>
            <DialogActions>
                <Button onClick={handleBack} variant="outlined">
                    {t('common:cancel')}
                </Button>
                <Button onClick={handleRecommend}>{t('common:invite')}</Button>
            </DialogActions>
        </>
    );
};

export default AddContactBlock;
